import { Ajax } from "../Common/Ajax";
import { GlobalData } from "../Common/GlobalData";
import { HtmlHelpers } from "../Common/HtmlHelpers";
import { ChartAxes, ReportDateHelper, TemplateHelpers } from "../Common/KendoHelpers";

export class MovingAveragesHistory {
    private static readonly SYMBOL = '#symbol';
    private static readonly COMPANY_NAME = '#companyName';
    private static readonly CHART = '#chart';
    private static readonly GRID = '#grid';

    private dataSourceOptions!: kendo.data.DataSourceOptions;

    constructor(symbol: string) {
        this.init(symbol);
    }

    private init(symbol: string) {
        const self = this;

        const symbolElement = $(MovingAveragesHistory.SYMBOL);
        symbolElement.kendoMaskedTextBox();
        if (symbol && symbol.length > 0) {
            $(MovingAveragesHistory.SYMBOL).val(symbol.toUpperCase());
        }

        $(MovingAveragesHistory.SYMBOL).on('keyup', (event: any) => {
            if (HtmlHelpers.enterKeyPressed(event)) {
                const symbolEntered = $(MovingAveragesHistory.SYMBOL).val()?.toString();
                const symbol = symbolEntered ?? null;
                $(MovingAveragesHistory.SYMBOL).val(symbol?.toUpperCase() ?? '');

                this.updateCompanyName(symbol);

                const grid = $(MovingAveragesHistory.GRID).data('kendoGrid')!;
                if (symbol && symbol.length > 0) {
                    this.setDataSourceOptions(symbol);
                    grid.setDataSource(new kendo.data.DataSource(this.dataSourceOptions));
                }
            }
        });

        this.setDataSourceOptions(symbol);

        $(MovingAveragesHistory.GRID).kendoGrid({
            dataSource: new kendo.data.DataSource(this.dataSourceOptions),
            height: 915,
            groupable: false,
            pageable: false,
            dataBound: () => {
                // Set up the tooltip for each grid row.
                const data = $(MovingAveragesHistory.GRID).data('kendoGrid')!.dataSource.data();
                if (!data || data.length == 0) {
                    return;
                }

                ReportDateHelper.showReportDate(data[0].date);

                const rows = $(MovingAveragesHistory.GRID).find('tr');
                rows.each((index: number, rowElement: HTMLTableRowElement) => {
                    // Skip over the header row.
                    if (index > 0) {
                        const row = data[index - 1];
                        $(rowElement).kendoTooltip({
                            content: '<table>' +
                                self.getTooltipRow('Date',
                                    HtmlHelpers.getFormattedDate(row.date)) +
                                self.getTooltipRow('SMA(10)',
                                    HtmlHelpers.getFormattedCurrency(row.tenDaySma)) +
                                self.getTooltipRow('EMA(21)',
                                    HtmlHelpers.getFormattedCurrency(row.twentyOneDayEma)) +
                                self.getTooltipRow('SMA(50)',
                                    HtmlHelpers.getFormattedCurrency(row.fiftyDaySma)) +
                                self.getTooltipRow('SMA(200)',
                                    HtmlHelpers.getFormattedCurrency(row.twoHundredDaySma)) +
                                '</table>',
                            showOn: 'click',
                        });
                    }
                });

                this.initChart(data);
            },
            columns: [{
                field: 'date',
                title: 'Date',
                width: '10%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                template: kendo.template(
                    TemplateHelpers.getFormattedDateTemplate('date')),
            }, {
                field: 'close',
                title: 'Close',
                width: '9%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                format: '{0:c2}',
            }, {
                field: 'volume',
                title: 'Volume',
                width: '10%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                format: '{0:n0}',
            }, {
                field: 'closeUpDown',
                title: 'Close<br>Up/Down',
                width: '9%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                template: kendo.template(TemplateHelpers.getBgColorCurrencyTemplate('closeUpDown')),
            }, {
                field: 'tenDaySma',
                hidden: true,
            }, {
                field: 'tenDaySmaPct',
                title: '10-Day<br>SMA %',
                width: '9%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                template: kendo.template(TemplateHelpers.getBgColorPercentTemplate('tenDaySmaPct', 1)),
            }, {
                field: 'twentyOneDayEma',
                hidden: true,
            }, {
                field: 'twentyOneDayEmaPct',
                title: '21-Day<br>EMA %',
                width: '9%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                template: kendo.template(TemplateHelpers.getBgColorPercentTemplate('twentyOneDayEmaPct', 1)),
            }, {
                field: 'fiftyDaySma',
                hidden: true,
            }, {
                field: 'fiftyDaySmaPct',
                title: '50-Day<br>SMA %',
                width: '9%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                template: kendo.template(TemplateHelpers.getBgColorPercentTemplate('fiftyDaySmaPct', 1)),
            }, {
                field: 'oneHundredFiftyDaySma',
                hidden: true,
            }, {
                field: 'oneHundredFiftyDaySmaPct',
                title: '150-Day<br>SMA %',
                width: '9%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                template: kendo.template(TemplateHelpers.getBgColorPercentTemplate('oneHundredFiftyDaySmaPct', 1)),
            }, {
                field: 'twoHundredDaySma',
                hidden: true,
            }, {
                field: 'twoHundredDaySmaPct',
                title: '200-Day<br>SMA %',
                width: '9%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                template: kendo.template(TemplateHelpers.getBgColorPercentTemplate('twoHundredDaySmaPct', 1)),
            }, {
                field: 'volumeGtTenDays',
                title: 'Volume > <br>10 Days',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                template: kendo.template(TemplateHelpers.getGreenBoolTemplate('volumeGtTenDays')),
            }, {
                field: 'maRank',
                title: 'Moving Avg<br>Rank',
                width: '9%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                format: '{0:p0}',
            }]
        });
    }

    private updateCompanyName(symbol: string | null) {
        const success = (companyName: string) => {
            $(MovingAveragesHistory.COMPANY_NAME).html('&nbsp;&nbsp;' + companyName);
        };

        Ajax.invoke(
            GlobalData.URL_SUFFIX_GENERAL_DATA_COMPANY_NAME +
            '?symbol=' + symbol, 'GET', null, success);
    }

    private setDataSourceOptions(symbol: string) {
        if (symbol && symbol.length > 0) {
            this.dataSourceOptions = {
                transport: {
                    read: GlobalData.URL_BASE +
                        GlobalData.URL_SUFFIX_TECHNICAL_REPORTS_MOVING_AVERAGES_HISTORY +
                        '?symbol=' + symbol,
                },
            }
        }
    }

    private getTooltipRow(name: string, value: string) {
        return '<tr class="mah-tooltip-tr"><td class="mah-tooltip-td-left">' +
            name + '</td><td class="mah-tooltip-td-right">' +
            value + '</td></tr>';
    }

    private initChart(data: any) {
        const symbol = $(MovingAveragesHistory.SYMBOL).val()?.toString();

        const closeLine = new Array<ChartAxes>();
        const tenDayLine = new Array<ChartAxes>();
        const twentyOneDayLine = new Array<ChartAxes>();
        const fiftyDayLine = new Array<ChartAxes>();
        const oneHundredFiftyDayLine = new Array<ChartAxes>();
        let min = Number.MAX_VALUE;
        let max = 0;

        for (const row of data) {
            closeLine.unshift(new ChartAxes(row.date, row.close));
            tenDayLine.unshift(new ChartAxes(row.date, row.tenDaySma));
            twentyOneDayLine.unshift(new ChartAxes(row.date, row.twentyOneDayEma));
            fiftyDayLine.unshift(new ChartAxes(row.date, row.fiftyDaySma));
            oneHundredFiftyDayLine.unshift(new ChartAxes(row.date,
                row.oneHundredFiftyDaySma));

            if (row.close < min) {
                min = row.close;
            }
            if (row.close > max) {
                max = row.close;
            }
            if (row.tenDaySma < min) {
                min = row.tenDaySma;
            }
            if (row.tenDaySma > max) {
                max = row.tenDaySma;
            }
            if (row.twentyOneDayEma < min) {
                min = row.twentyOneDayEma;
            }
            if (row.twentyOneDayEma > max) {
                max = row.twentyOneDayEma;
            }
            if (row.fiftyDaySma < min) {
                min = row.fiftyDaySma;
            }
            if (row.fiftyDaySma > max) {
                max = row.fiftyDaySma;
            }
            if (row.oneHundredFiftyDaySma < min) {
                min = row.oneHundredFiftyDaySma;
            }
            if (row.oneHundredFiftyDaySma > max) {
                max = row.oneHundredFiftyDaySma;
            }
        }

        min = Math.floor(min);
        max = Math.ceil(max);

        $(MovingAveragesHistory.CHART).kendoChart({
            title: {
                text: symbol + " Stock Price vs. Moving Averages"
            },
            legend: {
                position: "bottom"
            },
            series: [{
                type: "line",
                markers: {
                    visible: false
                },
                data: closeLine,
                name: symbol + " Price",
                color: "blue",
                axis: "price",
                field: "value",
                categoryField: "date",
                tooltip: {
                    visible: true,
                    template: "#= kendo.format('{0:MM/dd/yy}', category) #",
                }
            }, {
                type: "line",
                markers: {
                    visible: false
                },
                data: tenDayLine,
                name: "10-Day SMA",
                color: "#40e803", // green
                axis: "price"
            }, {
                type: "line",
                markers: {
                    visible: false
                },
                data: twentyOneDayLine,
                name: "21-Day EMA",
                color: "#ce86f2", // light purple
                axis: "price"
            }, {
                type: "line",
                markers: {
                    visible: false
                },
                data: fiftyDayLine,
                name: "50-Day SMA",
                color: "red",
                axis: "price"
            }, {
                type: "line",
                markers: {
                    visible: false
                },
                data: oneHundredFiftyDayLine,
                name: "150-Day SMA",
                color: "grey",
                axis: "price"
            }],
            valueAxis: [{
                name: "price",
                color: "black",
                min: min,
                max: max
            }],
        });
    }
}
